import moment from "moment";
import { Link } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import RemoveIcon from "@mui/icons-material/Remove";
import ScheduleIcon from "@mui/icons-material/Schedule";
import BlockIcon from "@mui/icons-material/Block";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { Box } from "@mui/material";

export const getCurrence=(value)=>{
    return  value? '$'+value:''
  }
// Status pill renderer consolidating previous merged/insolvent/suspWith/isActive flags
const renderStatusPill = (params) => {
  const raw = params?.value || '';
  const value = String(raw).trim();
  const lower = value.toLowerCase();
  const cfgMap = {
    licensed: {
      bg: '#dff6dd',
      color: 'success.dark',
      icon: <CheckCircleIcon fontSize="inherit" />
    },
    deferred: {
      bg: '#fff4ce',
      color: '#8a6d00',
      icon: <ScheduleIcon fontSize="inherit" />
    },
    inactive: {
      bg: '#e5e7eb',
      color: 'text.secondary',
      icon: <RemoveIcon fontSize="inherit" />
    },
    suspended: {
      bg: '#fde7e9',
      color: '#a4262c',
      icon: <BlockIcon fontSize="inherit" />
    },
    insolvent: {
      bg: '#fde7e9',
      color: '#a4262c',
      icon: <ReportProblemIcon fontSize="inherit" />
    },
    merged: {
      bg: '#dff6dd',
      color: 'success.dark',
      icon: <MergeTypeIcon fontSize="inherit" />
    }
  };
  const cfg = cfgMap[lower] || {
    bg: 'grey.300',
    color: 'text.primary',
    icon: <RemoveIcon fontSize="inherit" />
  };
  return (
    <Box component="span" sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1,
      py: 0.25,
      borderRadius: '15px',
      fontSize: 12,
      fontWeight: 600,
      bgcolor: cfg.bg,
      color: cfg.color,
      textTransform: 'capitalize',
      lineHeight: 1,
      minWidth: 70,
      justifyContent: 'center'
    }}>
      {cfg.icon}
      {value || 'N/A'}
    </Box>
  );
};

// Pill for Assessment Type (Class A / Class B)
const renderAssessmentTypePill = (params) => {
  const raw = params?.value;
  // Support both numeric keys and already-transformed strings
  const label = raw === 1 || raw === '1' || String(raw).toLowerCase() === 'class a' ? 'Class A' : 'Class B';
  const isA = label === 'Class A';
  const cfg = isA ? {
    bg: '#dff6dd',
    color: 'success.dark'
  } : {
    bg: '#ddeeff',
    color: '#003366'
  };
  return (
    <Box component="span" sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 1,
      py: 0.25,
      borderRadius: '15px',
      fontSize: 12,
      fontWeight: 600,
      bgcolor: cfg.bg,
      color: cfg.color,
      lineHeight: 1,
      minWidth: 70,
      textTransform: 'none'
    }}>
      {label}
    </Box>
  );
};

export const memberColumns = [
  // Main identifying info
  {
    field: "naicCode",
    headerName: "NAIC Code",
    width: 75,
    editable: true,
  },
  {
    field: "memberName",
    headerName: "Member Name",
    width: 300,
    minWidth: 200,
    editable: true,
    renderCell: (params) => (
      <Link className="link" to={`/member?id=${params.id}`}>
        {params.value}
      </Link>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    width: 110,
    align: 'center',
    headerAlign: 'center',
    renderCell: renderStatusPill,
  },
  {
    field: "groupCode",
    headerName: "Group Code",
    width: 110,
    editable: false,
  },
  {
    field: "groupName",
    headerName: "Group Name",
    width: 300,
    minWidth: 200,
    flex: 1,
    editable: false,
  },

  // Contact and email info (kept earlier in the table)
  //{ field: "phone", headerName: "Phone", width: 100, editable: true },
  {
    field: "proxyEmail",
    headerName: "Proxy Email",
    width: 300,
    minWidth: 200,
    editable: true,
    flex: 1,
  },
  {
    field: "asmtEmail",
    headerName: "Asmt. Email",
    width: 300,
    minWidth: 200,
    editable: true,
    flex: 1,
  },

  // Numeric metrics
  //{ field: "invoiced", headerName: "Invoiced", width: 75, editable: true },
  //{ field: "payments", headerName: "Payments", width: 75, editable: true },
  {
    field: "balanceDue",
    headerName: "Balance Due",
    width: 90,
    editable: true,
    renderCell: (params) => {
      const val = params?.value || 0;
      const display = val ? `$${val}` : "$0";
      if (val > 0) {
        return (
          <Box
            component="span"
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 2,
              fontSize: 12,
              lineHeight: 1.2,
              fontWeight: 600,
              bgcolor: "#fed9cc",
              color: "#d83b01",
              display: 'inline-block',
              minWidth: 'fit-content',
              textAlign: 'center'
            }}
          >
            {display}
          </Box>
        );
      }
      return display;
    }
  },

  // Removed old merged / insolvent / suspWith / isActive columns in favor of consolidated status
];

  export const addMemberGroupColumns = [
    {
      field: "naicCode",
      headerName: "NAIC Code",
      width: 75,
      editable: true,
    },
    {
      field: "memberName",
      headerName: "Member Name",
      width: 300,
      minWidth: 200,
      editable: true,
    },
    {
      field: "proxyEmail",
      headerName: "Email",
      width: 300,
      minWidth: 200,
      editable: true,
      flex: 1,
    },
  ];

 export const insolventColumns = [
    { field: 'naicCode', headerName: 'NAIC Code', width: 100, editable: true, flex: 1 },
    { field: 'insolventCompany', headerName: 'Insolvent Company', width: 200, editable: true,  },
    { field: 'foldte', headerName: 'FOL DTE', width: 100, editable: true, flex: 1,  },
    { field: 'discharged', headerName: 'Discharged', width: 180, editable: true ,flex: 1 },
    { field: 'asmntDate', headerName: 'Asmnt Date', width: 100, editable: true,  },
    { field: 'asmnAmount', headerName: 'Asmnt Amount', width: 250, editable: true, flex: 1, valueGetter: getCurrence },
    { field: 'callDate', headerName: 'Call Date', width: 250, editable: true, flex: 1 },
    { field: 'callAmount', headerName: 'Call Amount', width: 150, editable: true, flex: 1 , valueGetter: getCurrence,},
    { field: 'life', headerName: 'Life', width: 150, editable: true, flex: 1, valueGetter: getCurrence, },
    { field: 'health', headerName: 'Health', width: 150, editable: true, flex: 1, valueGetter: getCurrence, },
    { field: 'annuity', headerName: 'Annuity', width: 150, editable: true, flex: 1, valueGetter: getCurrence, },
    { field: 'bal', headerName: 'Bal To Be Called', width: 150, editable: true, flex: 1 , valueGetter: getCurrence,},
];

export const assessmentColumns = [
  { field: 'naicCode', headerName: 'NAIC Code', width: 70},
  { field: 'assessmentTypeKey', headerName: 'Assessment Type', width: 130, renderCell: renderAssessmentTypePill },
  { field: 'title', headerName: 'Title', width: 400},
  { field: 'premiumYear', headerName: 'Premium Year', width: 75, editable: true },
  { field: 'assessmentDate', headerName: 'Assessment Date', width: 100, valueGetter: (value) => value ? moment(value).utc().format("MM/DD/YYYY") : 'N/A' },
  { field: 'callAmountsLife', headerName: 'Call Amounts Life', width: 100, valueGetter: getCurrence },
  { field: 'callAmountsHealth', headerName: 'Call Amounts Health', width: 100, valueGetter: getCurrence },
  { field: 'callAmountsAllocatedAnnuity', headerName: 'Call Amounts Allocated Annuity', width: 100, valueGetter: getCurrence },
  { field: 'callAmountsUnallocatedAnnuity', headerName: 'Call Amounts Unallocated Annuity', width: 100, valueGetter: getCurrence },
  { field: 'minimumInvoice', headerName: 'Minimum Invoice', width: 100, valueGetter: getCurrence},
  { field: 'comments', headerName: 'Comments', width: 100},
  { field: "adminAmount", headerName: "Admin Amount", width: 75, valueGetter: getCurrence },
  { field: "total", headerName: "Total", width: 75, editable: true, valueGetter: getCurrence },
  { field: 'statistics',
    headerName: 'Statistics',
    width: 150,
    flex: 1,
    valueGetter: (value) => value?.totalInvoices ? `${value?.totalInvoices} invoices out of ${value?.paidInvoices} are paid, count of overdue: ${value?.overdueInvoices}` : ''},
]; 

export const paymentsColumns = [
  {
  field: "id",
  headerName: "Payment Id",
  width: 75,
},
{
  field: "parentPaymentId",
  headerName: "Parent Payment Id",
  width: 200,
},
    {
      field: "naicCode",
      headerName: "NAIC Code",
      width: 75,
    },
    {
      field: "memberName",
      headerName: "Member Name",
      width: 300,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "checkNumber",
      headerName: "Check Number",
      width: 200,
    },
    {
      field: "dateReceived",
      headerName: "Date Received",
      width: 100,
      valueGetter: (value) =>
        value ? moment(value).utc().format("MM/DD/YYYY") : 'N/A',
    },
    {
      field: "paymentAmount",
      headerName: "Payment Amount",
      width: 100,
      valueFormatter:getCurrence,
    },
    {
      field: "appliedAmount",
      headerName: "Applied Amount",
      width: 100,
      valueFormatter:getCurrence,
    },
    {
      field: "comments",
      headerName: "Comments",
      width: 250,
      flex: 1,
    },
    {
      field: "assessmentDate",
      headerName: "Assessment Date",
      width: 100,
      valueGetter: (value) =>
        value ? moment(value).utc().format("MM/DD/YYYY") : 'N/A',
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      width: 100,
    },
  ];

export  const invoicesColumns = [
    {
      field: "naicCode",
      headerName: "NAIC Code",
      width: 75,
      editable: true,
    },
    {
      field: "memberName",
      headerName: "Member Name",
      width: 300,
      minWidth: 200,
      editable: true,
      flex: 1,
    },
    {
      field: "invoiceDate",
      headerName: "Date",
      width: 100,
      minWidth: 75,
      editable: true,
      valueGetter: (value) =>
        value ? moment(value).utc().format("MM/DD/YYYY") : 'N/A',
    },
    {
      field: "period",
      headerName: "Period",
      width: 75,
      editable: true,
    },
    {
      field: "assessmentType",
      headerName: "Assessment Type",
      minWidth: 100,
      width: 150,
      editable: true,
      flex: 1,
    },
    {
      field: "status",
      headerName: "Status",
      width: 75,
      editable: true,
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 75,
      editable: true,
    },
    {
      field: "noticeNumber",
      headerName: "Notice number",
      width: 75,
      editable: true,
    },
  ];

export const premiumsColumns = [
  { field: 'naicCode', headerName: 'NAIC Code', width: 90 },
  { field: 'memberName', headerName: 'Member Name', width: 300, minWidth: 200, flex: 1 },
  { field: 'processingYear', headerName: 'Processing Year', width: 130 },
  { field: 'companyAmountsAno', headerName: 'Annuity', width: 120, valueGetter: getCurrence },
  { field: 'companyAmountsHno', headerName: 'Health', width: 120, valueGetter: getCurrence },
  { field: 'companyAmountsLno', headerName: 'Life', width: 120, valueGetter: getCurrence },
  { field: 'companyAmountsUno', headerName: 'Unallocated', width: 130, valueGetter: getCurrence },
];

  export const affiliatesColumns = [
    {
      field: "naicCode",
      headerName: "NAIC Code",
      width: 75,
      editable: true,
    },
    {
      field: "memberName",
      headerName: "Member Name",
      width: 300,
      minWidth: 200,
      editable: true,
      flex: 1,
      renderCell: (params) => (
        <Link className="link" to={`/member?id=${params.id}`}>
          {params.value}
        </Link>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      width: 250,
      minWidth: 200,
      editable: true,
      flex: 1,
    },
    {
      field: "active",
      headerName: "Active",
      width: 75,
      editable: true,
    }
  ];
